import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '../../components/ui/sidebar';
import { Separator } from '../../components/ui/separator';
import { Shield } from 'lucide-react';
import { AppSidebar } from './app-sidebar';
import { MobileNav } from './mobile-nav';
import { PerformanceMonitor } from '../../components/ui/performance-monitor';
import { ErrorReporter } from '../../components/ui/error-reporter';
import { useAuthStore } from '../../app/store/auth-store';


interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <SidebarProvider>
      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <AppSidebar />
      </div>
      <SidebarInset className="md:ml-0">
        {/* Header */}
        <header className="flex h-14 sm:h-16 shrink-0 items-center gap-2 border-b bg-background px-3 sm:px-4">
          <div className="flex items-center gap-2 w-full">
            {/* Desktop Sidebar Trigger */}
            <SidebarTrigger className="-ml-1 hidden md:flex" />
            
            {/* Mobile Navigation */}
            <div className="md:hidden">
              <MobileNav 
                open={mobileNavOpen} 
                onOpenChange={setMobileNavOpen} 
              />
            </div>
            
            <Separator orientation="vertical" className="mr-2 h-4 hidden md:block" />
            
            {/* App Title for mobile */}
            <div className="flex items-center gap-2 md:hidden">
              <Shield className="h-5 w-5 text-primary" />
              <span className="font-semibold text-base">Admin Panel</span>
            </div>
            
            {/* Page Title - desktop only */}
            <div className="flex-1 hidden md:block">
              <h1 className="text-lg font-semibold">
                {t('dashboard.title')}
              </h1>
            </div>

            {/* User info for mobile */}
            <div className="flex-1 md:hidden" />
            {user && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground md:hidden">
                <span className="truncate max-w-[100px] sm:max-w-[120px]">
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user.email
                  }
                </span>
              </div>
            )}
          </div>
        </header>
        
        {/* Main Content */}
        <div className="flex flex-1 flex-col gap-3 sm:gap-4 p-3 sm:p-4 pt-0">
          {children}
        </div>
      </SidebarInset>
      
      {/* Performance Monitor (development only) */}
      <PerformanceMonitor />
      
      {/* Error Reporter (development only) */}
      <ErrorReporter />
    </SidebarProvider>
  );
};